import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Button, RadioGroup } from "@truevault/aquilo";
import type { AutocompleteItem } from "@truevault/aquilo/src/components/textInput/Autocomplete";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { CONTRACTOR_CATEGORY, VENDOR_CATEGORY } from "../../../types/Vendor";
import {
  useCreateManagedAdNetwork,
  useDeleteManagedAdNetworkByIds,
  useManagedAdNetworksByNetwork,
} from "../../../hooks/useManagedAdNetworks";
import type { VendorInventoryItem } from "./vendorInventoryUtils";

/** Vendors that can manage an ad network's custom audience lists (e.g. Klaviyo). */
export const MANAGER_VENDOR_CATEGORIES = new Set([
  VENDOR_CATEGORY.SALES_MARKETING_TOOL,
  VENDOR_CATEGORY.CO_MARKETING_PARTNER,
  CONTRACTOR_CATEGORY.SALES_MARKETING,
]);

export function ManagedByVendorSection({
  radioGroupNamePrefix,
  org,
  vendor,
  allVendors,
  portalAutocompleteDropdown = false,
  onManagerAdded,
}: {
  radioGroupNamePrefix: string;
  org: OrganizationDto;
  vendor: VendorInventoryItem;
  allVendors: VendorInventoryItem[];
  portalAutocompleteDropdown?: boolean;
  onManagerAdded?: () => void;
}) {
  const networkId = parseInt(vendor.recipientData!.id);

  const { data: managers = [], isLoading } = useManagedAdNetworksByNetwork(org.id, networkId);
  const { mutateAsync: createManagedAdNetwork } = useCreateManagedAdNetwork(org.id);
  const { mutateAsync: deleteManagedAdNetworkByIds } = useDeleteManagedAdNetworkByIds(org.id, {
    syncSurvey: true,
  });

  // Track user intent independently of manager list (so "Yes" persists before any manager is added)
  const [intentToShow, setIntentToShow] = useState<boolean | null>(null);
  // Defer to loading state to avoid a "No" flash before the query resolves
  const isManagedByVendor = isLoading
    ? null
    : intentToShow !== null
    ? intentToShow
    : managers.length > 0;

  // Key to force-reset the Autocomplete input after each selection
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  // Wait for the selected manager to render before notifying the parent.
  const [pendingManagerId, setPendingManagerId] = useState<string | null>(null);

  useEffect(() => {
    if (pendingManagerId && managers.some((manager) => manager.managerId === pendingManagerId)) {
      onManagerAdded?.();
      setPendingManagerId(null);
    }
  }, [managers, onManagerAdded, pendingManagerId]);

  const autocompleteItems: AutocompleteItem[] = useMemo(() => {
    const addedManagerIds = new Set(managers.map((manager) => manager.managerId));
    return allVendors
      .filter(
        (candidate) =>
          candidate.isRecipient &&
          candidate.recipientData &&
          candidate.vendorId !== vendor.vendorId &&
          MANAGER_VENDOR_CATEGORIES.has(candidate.category) &&
          !addedManagerIds.has(candidate.recipientData.id),
      )
      .map((candidate) => ({
        id: candidate.recipientData!.id,
        name: candidate.name,
        logoUrl: candidate.logoUrl,
      }));
  }, [allVendors, managers, vendor.vendorId]);

  return (
    <div className="flex justify-between items-start gap-[12px] mt-[12px]">
      <div className="pt-[6px] flex-shrink-0">
        <svg
          width="12"
          height="13"
          viewBox="0 0 12 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11.25 8.26172L6.75 12.7617L5.69531 11.707L8.40234 9H0V0H1.51172V7.52344H8.40234L5.69531 4.81641L6.75 3.76172L11.25 8.26172Z"
            fill="black"
          />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start gap-[12px]">
          <div className="text-gray-700 font-medium">
            Audience lists in {vendor.name} are managed by another vendor
          </div>
          <RadioGroup
            name={`${radioGroupNamePrefix}-managedByVendor`}
            direction="horizontal"
            options={[
              { label: "Yes", value: "YES" },
              { label: "No", value: "NO" },
            ]}
            selectedValue={
              isManagedByVendor === null ? undefined : isManagedByVendor ? "YES" : "NO"
            }
            onChange={async (newValue: string) => {
              if (newValue === "YES") {
                setIntentToShow(true);
              } else {
                setIntentToShow(false);
                await Promise.all(
                  managers.map((m) =>
                    deleteManagedAdNetworkByIds({
                      managerId: parseInt(m.managerId),
                      networkId,
                    }),
                  ),
                );
              }
            }}
          />
        </div>

        {isManagedByVendor === true && (
          <div className="mt-[10px]">
            <Autocomplete
              key={autocompleteKey}
              items={autocompleteItems}
              selectedItem={null}
              // show all possible options initially
              initialSuggestionsCount={autocompleteItems.length}
              onSelect={async (item) => {
                await createManagedAdNetwork({
                  managerId: item.id,
                  networkId: networkId.toString(),
                  syncSurvey: true,
                });
                setPendingManagerId(item.id);
                setAutocompleteKey((k) => k + 1);
              }}
              leadingElement={
                <div className="w-[50px] h-full border border-solid border-neutral-400 rounded-l-sm flex items-center justify-center">
                  <MagnifyingGlassIcon className="w-[18px] h-[18px] text-neutral-500" />
                </div>
              }
              leadingInputPaddingPx={66}
              dropdownWidth="w-[417px]"
              portalDropdown={portalAutocompleteDropdown}
            />

            {managers.length > 0 && (
              <div className="mt-[8px] space-y-[6px]">
                {managers.map((manager) => {
                  const managerVendor = allVendors.find(
                    (v) => v.isRecipient && v.recipientData?.id === manager.managerId,
                  );
                  return (
                    <div
                      key={manager.id}
                      className="flex items-center justify-between border border-solid border-neutral-200 rounded-[4px] px-[12px] py-[8px]"
                    >
                      <div className="flex items-center gap-[10px]">
                        {managerVendor?.logoUrl && (
                          <img
                            src={managerVendor.logoUrl}
                            alt={manager.managerName ?? managerVendor.name}
                            className="h-6 w-6"
                          />
                        )}
                        <span className="text-[14px] font-medium text-gray-900">
                          {manager.managerName ?? managerVendor?.name ?? "Unknown Vendor"}
                        </span>
                      </div>
                      <Button
                        color="tertiary"
                        size="s"
                        onClick={async () => {
                          await deleteManagedAdNetworkByIds({
                            managerId: parseInt(manager.managerId),
                            networkId,
                          });
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
